import {
  childrenPropsSDKFactory,
  clickPropsSDKFactory,
  composeSDKFactories,
  createAccessibilityPropSDKFactory,
  createElementPropsSDKFactory,
  focusPropsSDKFactory,
  registerCorvidEvent,
  reportWarning,
  toJSONBase,
  withValidation,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations';
import type { $W } from '@wix/thunderbolt-symbols';
import type {
  IAccordionContainerSDKFactory,
  IAccordionContainerPropsSDKFactory,
  IOnItemClickedComponentEvent,
  IOnItemClickedCorvidEvent,
  IRawItem,
  IAccordionContainerPlatformProps,
} from '../types';
import {
  customValidators,
  idShouldBePresentWithinInitialItemsValidator,
  itemWithIdShouldBePresentWithinInitialItemsValidator,
  validationSchema,
} from './AccordionContainer.validations';

const accordionContainer: IAccordionContainerPropsSDKFactory = api => {
  const { props, setProps, createSdkState, metaData } = api;
  const [state] = createSdkState({ initialItems: props.items });

  const sdkProps: ReturnType<IAccordionContainerPropsSDKFactory> = {
    get items() {
      return props.items;
    },
    set items(items: Array<IRawItem>) {
      if (
        !itemWithIdShouldBePresentWithinInitialItemsValidator(
          state.initialItems,
          items,
        )
      ) {
        return;
      }
      const nextProps: { items: Array<IRawItem>; opened?: Array<string> } = {
        items: items.map(({ id, label }) => ({ id, label })),
      };
      const opened = props.opened.filter(id =>
        nextProps.items.some((item: IRawItem) => item.id === id),
      );

      if (opened.length !== props.opened.length) {
        nextProps.opened = opened;
      }

      setProps(nextProps);
    },
    get multipleExpand() {
      return props.multiple;
    },
    set multipleExpand(multiple: boolean | undefined) {
      setProps({ multiple: multiple ?? false });
    },
    get expandedItems() {
      return props.opened;
    },
    set expandedItems(opened: Array<string> | undefined) {
      if (!opened) {
        setProps({ opened: [] });
        return;
      }
      const uniqueIds = Array.from(new Set(opened));
      if (
        !idShouldBePresentWithinInitialItemsValidator(
          state.initialItems,
          uniqueIds,
        )
      ) {
        return;
      }
      if (!props.multiple && uniqueIds.length > 1) {
        reportWarning(
          `Accordion multipleExpand is set to false, but expandedItems array contains ${opened.length} items`,
        );
      }
      setProps({ opened: uniqueIds });
    },
    onItemClicked(cb: (event: IOnItemClickedCorvidEvent, $w: $W) => void) {
      return registerCorvidEvent<
        IOnItemClickedComponentEvent,
        IOnItemClickedCorvidEvent
      >('onItemClicked', api, cb, ({ componentEvent }) => componentEvent);
    },
    toJSON() {
      const { items, expandedItems, multipleExpand } = sdkProps;
      return {
        ...toJSONBase(metaData),
        items,
        expandedItems,
        multipleExpand,
      };
    },
  };

  return sdkProps;
};

const accordionContainerPropsSDKFactory = withValidation(
  accordionContainer,
  validationSchema,
  customValidators,
);
export const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  // TODO
  // enableAriaLabel: true,
  // enableAriaDescribedBy: true,
  // enableAriaLabelledBy: true,
  // enableAriaAtomic: true,
  // enableAriaBusy: true,
  // enableAriaCurrent: true,
  // enableAriaExpanded: true,
  // enableAriaLive: true,
  // enableAriaOwns: true,
  // enableAriaRelevant: true,
  // enableRole: true,
  // enableTabIndex: true,
  // enableAriaErrorMessage: true,
});
export const sdk = composeSDKFactories<
  IAccordionContainerPlatformProps,
  IAccordionContainerSDKFactory
>([
  createElementPropsSDKFactory(),
  clickPropsSDKFactory,
  childrenPropsSDKFactory,
  focusPropsSDKFactory,
  accessibilityPropsSDKFactory,
  accordionContainerPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
